import React, { createContext, useContext, useState, ReactNode } from 'react';

// Firebase auth is no longer used - all auth is via Supabase
// This context is kept as a stub to prevent import errors in pages that still reference it

interface UserData {
  uid: string;
  email: string | null;
  displayName: string | null;
  isAdmin: boolean;
  phoneNumber?: string | null;
  address?: string;
}

interface AuthContextType {
  currentUser: UserData | null;
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  isConfigured: boolean;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateUserProfile: (data: Partial<UserData>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser] = useState<UserData | null>(null);
  const [isAdmin] = useState(false);
  const [error] = useState<string | null>(null);

  const notConfigured = async () => {
    console.warn('Firebase auth is disabled. Use Supabase auth instead.');
  };

  const value: AuthContextType = {
    currentUser,
    isAdmin,
    isLoading: false,
    error,
    isConfigured: false,
    signUp: notConfigured,
    signIn: notConfigured,
    signOut: notConfigured,
    resetPassword: notConfigured,
    updateUserProfile: notConfigured,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
